import {
	type API_UpdateEnrollmentRequest,
	addCalendarDays,
	type BillingCycle,
	type CalendarDate,
	compareCalendarDates,
	EnrollmentStatus,
	todayIn,
} from "@pilatestime/shared-types";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Link,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import type { AxiosError } from "axios";
import {
	AlertTriangle,
	Archive,
	CalendarPlus,
	CreditCard,
	Edit2,
	ExternalLink,
	Info,
	Loader2,
	Pause,
	Play,
	RefreshCw,
	Save,
	X,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { fetchOrganizationCustomerPlansQueryOptions } from "@/api/customerPlans";
import {
	fetchEnrollmentByIdQueryOptions,
	useExtendEnrollment,
	usePauseEnrollment,
	useUnpauseEnrollment,
	useUpdateEnrollment,
} from "@/api/enrollments";
import {
	fetchOrganizationSchedulesQueryOptions,
	useDeleteSchedule,
} from "@/api/schedules";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { useEnrollmentExpiry } from "@/hooks/useEnrollmentExpiry";
import { parseCapacityExceededError } from "@/utils/capacityError";
import { calculateEndDate } from "@/utils/dateUtils";
import {
	calculateMonthlyAverage,
	formatCurrency,
} from "@/utils/planPricingUtils";
import { EnrollmentCancelationModal } from "../-components/EnrollmentCancelationModal";
import { EnrollmentExtendModal } from "../-components/EnrollmentExtendModal";
import { EnrollmentPauseModal } from "../-components/EnrollmentPauseModal";
import { EnrollmentResumeModal } from "../-components/EnrollmentResumeModal";
import { FilterableCardGrid } from "../-components/FilterableCardGrid";
import { ScheduleCard } from "../-components/ScheduleCard";
import {
	calculateWeeklySessionsFromSchedules,
	checkWeeklySessionsWithinLimit,
} from "../-utils/weeklySessionsValidation";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/enrollments/$enrollmentId/edit",
)({
	beforeLoad: ({ context }) => {
		if (!context.userId) {
			throw new Error("Not authenticated");
		}
	},
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);

		const [enrollment, schedules, customerPlans] = await Promise.all([
			context.queryClient.ensureQueryData(
				fetchEnrollmentByIdQueryOptions(
					userId,
					token,
					params.orgId,
					params.enrollmentId,
				),
			),
			context.queryClient.ensureQueryData(
				fetchOrganizationSchedulesQueryOptions(userId, token, params.orgId),
			),
			context.queryClient.ensureQueryData(
				fetchOrganizationCustomerPlansQueryOptions(userId, token, params.orgId),
			),
		]);

		return {
			userId,
			token,
			enrollment,
			schedules,
			customerPlans,
			orgId: params.orgId,
			enrollmentId: params.enrollmentId,
		};
	},
	component: EditEnrollmentPage,
});

function EditEnrollmentPage() {
	const { t } = useTranslation();
	const { formatDate } = useDateFormatter();
	const { isCloseToExpire } = useEnrollmentExpiry();
	const navigate = useNavigate();
	const { orgId, enrollmentId } = useParams({
		from: "/_auth/app/_app/org/$orgId/enrollments/$enrollmentId/edit",
	});
	const { userId, token } = Route.useLoaderData();

	// Fetch enrollment data
	const { data: enrollment } = useSuspenseQuery(
		fetchEnrollmentByIdQueryOptions(userId, token, orgId, enrollmentId),
	);

	// Fetch all schedules
	const { data: schedulesData } = useSuspenseQuery(
		fetchOrganizationSchedulesQueryOptions(userId, token, orgId),
	);

	// Fetch customer plans to get weeklySessionsLimit for the current plan
	const { data: customerPlansData } = useSuspenseQuery(
		fetchOrganizationCustomerPlansQueryOptions(userId, token, orgId),
	);

	// Local state for form fields
	const [selectedScheduleIds, setSelectedScheduleIds] = useState<string[]>(
		enrollment.schedules.map((s) => s.id),
	);
	const [notes, setNotes] = useState(enrollment.notes || "");

	// Schedule editing state
	const [isEditingSchedules, setIsEditingSchedules] = useState(false);
	const [tempSelectedScheduleIds, setTempSelectedScheduleIds] = useState<
		string[]
	>([]);

	const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
	const [isCanceling, setIsCanceling] = useState(false);
	const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);
	const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
	const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);

	// Original schedule IDs from the enrollment (for "Current" badge)
	const originalScheduleIds = useMemo(
		() => new Set(enrollment.schedules.map((s) => s.id)),
		[enrollment.schedules],
	);

	// Query client for manual invalidation
	const queryClient = useQueryClient();

	// Mutation hooks
	const updateEnrollmentMutation = useUpdateEnrollment();
	const deleteScheduleMutation = useDeleteSchedule();
	const pauseEnrollmentMutation = usePauseEnrollment();
	const unpauseEnrollmentMutation = useUnpauseEnrollment();
	const extendEnrollmentMutation = useExtendEnrollment();

	const isUpdating = updateEnrollmentMutation.isPending;
	const isPausing = pauseEnrollmentMutation.isPending;
	const isUnpausing = unpauseEnrollmentMutation.isPending;
	const isExtending = extendEnrollmentMutation.isPending;
	const canCancelEnrollment =
		enrollment.status !== EnrollmentStatus.CANCELED &&
		enrollment.status !== EnrollmentStatus.COMPLETED;
	const canPauseEnrollment = enrollment.status === EnrollmentStatus.ACTIVE;
	const canUnpauseEnrollment = enrollment.status === EnrollmentStatus.PAUSED;

	// Derived "class archived" state (D4): PAUSED with zero links to an active
	// schedule. This is a legal holding state, not an orphan — so the "no
	// schedule" surface below reads as informational, not action-needed.
	const isClassArchived =
		enrollment.status === EnrollmentStatus.PAUSED &&
		(enrollment.activeScheduleLinkCount ?? 0) === 0;

	// Renewal is allowed for active, paused, canceled, or expired enrollments (not pending or completed)
	const canRenewEnrollment = [
		EnrollmentStatus.ACTIVE,
		EnrollmentStatus.PAUSED,
		EnrollmentStatus.CANCELED,
		EnrollmentStatus.EXPIRED,
	].includes(enrollment.status);

	// Pay Now is only available for pending enrollments
	const canPayNow = enrollment.status === EnrollmentStatus.PENDING;

	// Extend is allowed for active, paused, or expired enrollments that have an end date
	const canExtendEnrollment =
		!!enrollment.endDate &&
		[
			EnrollmentStatus.ACTIVE,
			EnrollmentStatus.PAUSED,
			EnrollmentStatus.EXPIRED,
		].includes(enrollment.status);

	// Check if form has changes
	const hasChanges = useMemo(() => {
		const notesChanged = notes !== (enrollment.notes || "");
		const schedulesChanged =
			selectedScheduleIds.length !== enrollment.schedules.length ||
			!selectedScheduleIds.every((id) =>
				enrollment.schedules.some((s) => s.id === id),
			);
		return notesChanged || schedulesChanged;
	}, [notes, selectedScheduleIds, enrollment]);

	// Derive current customer plan and its weekly sessions limit
	const currentCustomerPlan = useMemo(
		() =>
			customerPlansData?.find((plan) => plan.id === enrollment.customerPlan.id),
		[customerPlansData, enrollment.customerPlan.id],
	);

	const weeklySessionsLimit = currentCustomerPlan?.weeklySessionsLimit;

	// Real-time validation for temp schedule selection (edit mode)
	const tempScheduleValidation = useMemo(() => {
		if (!isEditingSchedules) return null;

		const totalWeeklySessions = calculateWeeklySessionsFromSchedules(
			tempSelectedScheduleIds,
			schedulesData ?? [],
		);

		const hasLimit =
			typeof weeklySessionsLimit === "number" && weeklySessionsLimit > 0;
		const isExceeded = hasLimit && totalWeeklySessions > weeklySessionsLimit;

		return {
			totalWeeklySessions,
			weeklySessionsLimit: hasLimit ? weeklySessionsLimit : null,
			isExceeded,
		};
	}, [
		isEditingSchedules,
		tempSelectedScheduleIds,
		schedulesData,
		weeklySessionsLimit,
	]);

	// Handle save
	const handleSave = useCallback(async () => {
		const updateData: API_UpdateEnrollmentRequest = {};

		if (notes !== (enrollment.notes || "")) {
			updateData.notes = notes || undefined;
		}

		const schedulesChanged =
			selectedScheduleIds.length !== enrollment.schedules.length ||
			!selectedScheduleIds.every((id) =>
				enrollment.schedules.some((s) => s.id === id),
			);
		if (schedulesChanged) {
			const {
				isValid,
				weeklySessions,
				weeklySessionsLimit: effectiveWeeklySessionsLimit,
			} = checkWeeklySessionsWithinLimit(
				selectedScheduleIds,
				schedulesData ?? [],
				weeklySessionsLimit,
			);

			if (!isValid && effectiveWeeklySessionsLimit != null) {
				toast.error(
					t("enrollments.errors.weeklyLimitExceeded", {
						current: weeklySessions,
						limit: effectiveWeeklySessionsLimit,
					}),
				);
				return;
			}

			updateData.scheduleIds = selectedScheduleIds;
		}

		await updateEnrollmentMutation.mutateAsync({
			token,
			organizationId: orgId,
			enrollmentId,
			data: updateData,
			userId,
		});
	}, [
		notes,
		selectedScheduleIds,
		enrollment,
		schedulesData,
		token,
		orgId,
		enrollmentId,
		userId,
		updateEnrollmentMutation,
		t,
		weeklySessionsLimit,
	]);

	const handleOpenCancelModal = useCallback(() => {
		if (!canCancelEnrollment || isUpdating) return;
		setIsCancelModalOpen(true);
	}, [canCancelEnrollment, isUpdating]);

	const handleCloseCancelModal = useCallback(() => {
		if (isCanceling) return;
		setIsCancelModalOpen(false);
	}, [isCanceling]);

	const handleConfirmCancel = useCallback(
		async (scheduleIdsToDelete: string[]) => {
			setIsCanceling(true);
			try {
				await updateEnrollmentMutation.mutateAsync({
					token,
					organizationId: orgId,
					enrollmentId,
					data: {
						status: EnrollmentStatus.CANCELED,
					},
					userId,
				});

				for (const scheduleId of scheduleIdsToDelete) {
					await deleteScheduleMutation.mutateAsync({
						token,
						organizationId: orgId,
						scheduleId,
					});
				}

				// Invalidate queries to refresh the UI
				queryClient.invalidateQueries({
					queryKey: ["organization_enrollments", orgId],
				});
				queryClient.invalidateQueries({
					queryKey: ["organization_enrollments"],
				});
				queryClient.refetchQueries({
					queryKey: ["enrollment", orgId, enrollmentId, userId],
				});

				// Invalidate customer enrollments if applicable
				if (enrollment.customer?.id) {
					queryClient.invalidateQueries({
						queryKey: ["customer_enrollments", orgId, enrollment.customer.id],
					});
				}

				// Invalidate schedule-related queries
				queryClient.invalidateQueries({
					queryKey: ["organization_schedules", orgId],
				});
				queryClient.invalidateQueries({
					queryKey: ["scheduleSessions", "list", orgId],
				});
				queryClient.invalidateQueries({
					queryKey: ["scheduleSessions", "monthly", orgId],
				});
				queryClient.invalidateQueries({
					queryKey: ["attendance", "list"],
				});

				setIsCancelModalOpen(false);
			} catch (error) {
				console.error("Failed to cancel enrollment", error);
			} finally {
				setIsCanceling(false);
			}
		},
		[
			deleteScheduleMutation,
			orgId,
			enrollmentId,
			token,
			updateEnrollmentMutation,
			userId,
			queryClient,
			enrollment.customer?.id,
		],
	);

	const handleOpenPauseModal = useCallback(() => {
		setIsPauseModalOpen(true);
	}, []);

	const handleClosePauseModal = useCallback(() => {
		setIsPauseModalOpen(false);
	}, []);

	const handleConfirmPause = useCallback(async () => {
		if (!canPauseEnrollment || isPausing) return;

		toast.loading(t("enrollments.toasts.pausing"), { id: "pause-enrollment" });

		try {
			await pauseEnrollmentMutation.mutateAsync({
				token,
				organizationId: orgId,
				enrollmentId,
				userId,
			});
			toast.success(t("enrollments.toasts.pauseSuccess"), {
				id: "pause-enrollment",
			});
			setIsPauseModalOpen(false);
		} catch (error) {
			console.error("Failed to pause enrollment", error);
			toast.error(t("enrollments.toasts.pauseError"), {
				id: "pause-enrollment",
			});
		}
	}, [
		canPauseEnrollment,
		isPausing,
		pauseEnrollmentMutation,
		token,
		orgId,
		enrollmentId,
		userId,
		t,
	]);

	const handleOpenResumeModal = useCallback(() => {
		setIsResumeModalOpen(true);
	}, []);

	const handleCloseResumeModal = useCallback(() => {
		setIsResumeModalOpen(false);
	}, []);

	const handleConfirmResume = useCallback(
		async (attachScheduleId?: string) => {
			if (!canUnpauseEnrollment || isUnpausing) return;

			toast.loading(t("enrollments.toasts.unpausing"), {
				id: "unpause-enrollment",
			});

			try {
				await unpauseEnrollmentMutation.mutateAsync({
					token,
					organizationId: orgId,
					enrollmentId,
					userId,
					attachScheduleId,
				});
				toast.success(t("enrollments.toasts.unpauseSuccess"), {
					id: "unpause-enrollment",
				});
				setIsResumeModalOpen(false);
			} catch (error) {
				console.error("Failed to unpause enrollment", error);
				// Map the typed 409s (D3 gate) to specific copy; fall back otherwise.
				const capacity = parseCapacityExceededError(error);
				const errorCode = (error as AxiosError<{ error?: string }> | undefined)
					?.response?.data?.error;
				const message = capacity
					? t("common.errors.capacityExceededSchedule", {
							current: capacity.current,
							capacity: capacity.capacity,
						})
					: errorCode === "ENROLLMENT_MISSING_ACTIVE_SCHEDULE"
						? t("enrollments.unpause.missingActiveSchedule")
						: t("enrollments.toasts.unpauseError");
				toast.error(message, {
					id: "unpause-enrollment",
				});
			}
		},
		[
			canUnpauseEnrollment,
			isUnpausing,
			unpauseEnrollmentMutation,
			token,
			orgId,
			enrollmentId,
			userId,
			t,
		],
	);

	const handleOpenExtendModal = useCallback(() => {
		if (!canExtendEnrollment) return;
		setIsExtendModalOpen(true);
	}, [canExtendEnrollment]);

	const handleCloseExtendModal = useCallback(() => {
		if (isExtending) return;
		setIsExtendModalOpen(false);
	}, [isExtending]);

	const handleConfirmExtend = useCallback(
		async (newEndDate: CalendarDate, reason?: string) => {
			if (!canExtendEnrollment || isExtending) return;

			try {
				await extendEnrollmentMutation.mutateAsync({
					token,
					organizationId: orgId,
					enrollmentId,
					data: { newEndDate, reason },
					userId,
				});
				setIsExtendModalOpen(false);
			} catch (error) {
				console.error("Failed to extend enrollment", error);
			}
		},
		[
			canExtendEnrollment,
			isExtending,
			extendEnrollmentMutation,
			token,
			orgId,
			enrollmentId,
			userId,
		],
	);

	// Handle renewal navigation
	const handleRenew = useCallback(() => {
		if (!canRenewEnrollment) return;

		// Renewal periods are calendar days (design P1-c) — pure CalendarDate
		// arithmetic, so periodStart/periodEnd are exact and never drift a day.
		const today = todayIn("UTC");
		const currentEndDate = enrollment.endDate ?? null;

		// If the current period has not ended yet, start the day after it ends;
		// otherwise start today.
		const newStartDate: CalendarDate =
			currentEndDate && compareCalendarDates(today, currentEndDate) < 0
				? addCalendarDays(currentEndDate, 1)
				: today;

		// Calculate end date based on billing cycle
		const billingCycle = enrollment.customerPlan.billingCycle as BillingCycle;
		const newEndDate = calculateEndDate(newStartDate, billingCycle);

		// Navigate to billing/new with renewal params
		navigate({
			to: "/app/org/$orgId/billing/new",
			params: { orgId },
			search: {
				enrollmentId,
				renewal: true,
				periodStart: newStartDate,
				periodEnd: newEndDate,
			},
		});
	}, [
		canRenewEnrollment,
		enrollment.endDate,
		enrollment.customerPlan.billingCycle,
		navigate,
		orgId,
		enrollmentId,
	]);

	// Handle Pay Now navigation
	const handlePayNow = useCallback(() => {
		if (!canPayNow) return;

		navigate({
			to: "/app/org/$orgId/billing/new",
			params: { orgId },
			search: { enrollmentId },
		});
	}, [canPayNow, navigate, orgId, enrollmentId]);

	// Calculate monthly average for the plan
	const monthlyAverage = useMemo(() => {
		return calculateMonthlyAverage(
			enrollment.customerPlan.price.toString(),
			enrollment.customerPlan.billingCycle as BillingCycle,
		);
	}, [enrollment.customerPlan.price, enrollment.customerPlan.billingCycle]);

	const formattedPrice = useMemo(() => {
		return formatCurrency(
			enrollment.customerPlan.price,
			enrollment.customerPlan.currency,
		);
	}, [enrollment.customerPlan.price, enrollment.customerPlan.currency]);

	const formattedMonthlyAverage = useMemo(() => {
		return formatCurrency(monthlyAverage, enrollment.customerPlan.currency);
	}, [monthlyAverage, enrollment.customerPlan.currency]);

	const billingPeriod = useMemo(() => {
		return t(
			`customerPlans.billingCycle.${enrollment.customerPlan.billingCycle.toLowerCase()}`,
		);
	}, [enrollment.customerPlan.billingCycle, t]);

	const statusBadgeClasses: Record<EnrollmentStatus, string> = {
		[EnrollmentStatus.PENDING]: "badge-warning",
		[EnrollmentStatus.ACTIVE]: "badge-success",
		[EnrollmentStatus.PAUSED]: "badge-warning",
		[EnrollmentStatus.CANCELED]: "badge-error",
		[EnrollmentStatus.COMPLETED]: "badge-info",
		[EnrollmentStatus.EXPIRED]: "badge-neutral",
	};

	return (
		<div
			className="h-full flex flex-col space-y-4"
			data-testid="enrollment-form-container"
		>
			{/* Header: customer name, status, plan summary */}
			<div className="space-y-1">
				<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
					<h1 className="text-2xl font-semibold text-base-content">
						{enrollment.customer.firstName} {enrollment.customer.lastName}
					</h1>
					<span
						className={`badge ${statusBadgeClasses[enrollment.status as EnrollmentStatus] ?? ""}`}
						data-testid="enrollment-header-status"
					>
						{t(`enrollments.status.${String(enrollment.status).toLowerCase()}`)}
					</span>
				</div>
				<p className="text-sm text-base-content/60">
					{enrollment.customerPlan.name}
					<span className="font-mono">
						{" · "}
						{formattedPrice} / {billingPeriod}
					</span>
				</p>
			</div>

			{/* Actions */}
			<div className="flex flex-wrap items-center gap-2">
				<Button
					onClick={handleSave}
					disabled={
						isUpdating || !hasChanges || isPausing || isUnpausing || isExtending
					}
					size="sm"
					className="gap-2"
					data-testid="save-button"
				>
					{isUpdating ? (
						<>
							<Loader2 className="h-4 w-4 animate-spin" />
							{t("common.saving")}
						</>
					) : (
						<>
							<Save className="h-4 w-4" />
							{t("common.saveChanges")}
						</>
					)}
				</Button>
				{canPayNow && (
					<Button
						type="button"
						variant="default"
						size="sm"
						onClick={handlePayNow}
						disabled={
							isUpdating ||
							isCanceling ||
							isPausing ||
							isUnpausing ||
							isExtending
						}
						className="gap-2"
						data-testid="pay-now-button"
					>
						<CreditCard className="h-4 w-4" />
						{t("enrollments.actions.payNow")}
					</Button>
				)}
				{canPauseEnrollment && (
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={handleOpenPauseModal}
						disabled={
							isUpdating ||
							isCanceling ||
							isPausing ||
							isUnpausing ||
							isExtending
						}
						className="gap-2"
						data-testid="pause-enrollment-button"
					>
						{isPausing ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Pause className="h-4 w-4" />
						)}
						{t("enrollments.actions.pause")}
					</Button>
				)}
				{canUnpauseEnrollment && (
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={handleOpenResumeModal}
						disabled={
							isUpdating ||
							isCanceling ||
							isPausing ||
							isUnpausing ||
							isExtending
						}
						className="gap-2"
						data-testid="unpause-enrollment-button"
					>
						{isUnpausing ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Play className="h-4 w-4" />
						)}
						{t("enrollments.actions.unpause")}
					</Button>
				)}
				{canRenewEnrollment && (
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={handleRenew}
						disabled={
							isUpdating ||
							isCanceling ||
							isPausing ||
							isUnpausing ||
							isExtending
						}
						className="gap-2"
						data-testid="renew-enrollment-button"
					>
						<RefreshCw className="h-4 w-4" />
						{t("enrollments.actions.renew")}
					</Button>
				)}
				{canExtendEnrollment && (
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={handleOpenExtendModal}
						disabled={
							isUpdating ||
							isCanceling ||
							isPausing ||
							isUnpausing ||
							isExtending
						}
						className="gap-2"
						data-testid="extend-enrollment-button"
					>
						{isExtending ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<CalendarPlus className="h-4 w-4" />
						)}
						{t("enrollments.actions.extend")}
					</Button>
				)}
				{canCancelEnrollment && (
					<>
						<div className="grow" aria-hidden="true" />
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onClick={handleOpenCancelModal}
							disabled={
								isUpdating ||
								isCanceling ||
								isPausing ||
								isUnpausing ||
								isExtending
							}
							className="gap-2 text-error hover:bg-error hover:text-error-content"
							data-testid="cancel-enrollment-button"
						>
							<X className="h-4 w-4" />
							{t("enrollments.cancel.confirm")}
						</Button>
					</>
				)}
			</div>
			{/* Main Form Card */}
			<Card className="flex-1">
				<CardContent className="p-6 space-y-6">
					{/* Customer Plan - Read Only with Detailed Info */}
					<div className="space-y-4">
						<div>
							<p className="text-sm font-semibold text-base-content">
								{t("enrollments.form.customerPlan")}
							</p>
							<p className="text-xs text-base-content/60 mt-0.5">
								{enrollment.customerPlan.description}
							</p>
						</div>

						{/* Plan facts */}
						<dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
							<div>
								<dt className="text-xs text-base-content/60">
									{t("common.price")}
								</dt>
								<dd className="font-medium font-mono">{formattedPrice}</dd>
							</div>
							<div>
								<dt className="text-xs text-base-content/60">
									{t("common.billingCycle")}
								</dt>
								<dd className="font-medium">{billingPeriod}</dd>
							</div>
							<div>
								<dt className="text-xs text-base-content/60">
									{t("enrollments.form.startDate")}
								</dt>
								<dd className="font-medium font-mono">
									{formatDate(new Date(enrollment.startDate).getTime())}
								</dd>
							</div>
							<div>
								<dt className="text-xs text-base-content/60">
									{t("enrollments.form.endDate")}
								</dt>
								<dd className="font-medium font-mono">
									{enrollment.endDate
										? formatDate(new Date(enrollment.endDate).getTime())
										: t("enrollments.table.noEndDate")}
								</dd>
								{enrollment.endDate && isCloseToExpire(enrollment.endDate) && (
									<span className="badge badge-warning badge-xs gap-1 whitespace-nowrap mt-1">
										<AlertTriangle className="w-3 h-3" />
										{t("enrollments.badges.closeToExpire")}
									</span>
								)}
							</div>
							{enrollment.customerPlan.billingCycle !== "MONTHLY" && (
								<div>
									<dt className="text-xs text-base-content/60">
										{t("common.monthlyAverage")}
									</dt>
									<dd className="font-medium font-mono">
										{formattedMonthlyAverage}
										<span className="text-xs font-normal text-base-content/60 font-sans">
											/{t("common.month")}
										</span>
									</dd>
								</div>
							)}
							{typeof weeklySessionsLimit === "number" &&
								weeklySessionsLimit > 0 && (
									<div>
										<dt className="text-xs text-base-content/60">
											{t("common.weeklySessionsLimit")}
										</dt>
										<dd className="font-medium">
											{t("enrollments.validation.sessionsPerWeek", {
												count: weeklySessionsLimit,
											})}
										</dd>
									</div>
								)}
						</dl>

						{/* Pause history — only when the enrollment has ever been paused */}
						{enrollment.pausedAt && (
							<dl
								className={`grid grid-cols-2 gap-x-6 gap-y-3 rounded-md p-3 text-sm sm:grid-cols-3 ${
									enrollment.status === EnrollmentStatus.PAUSED
										? "border border-warning/25 bg-warning/10"
										: "bg-base-200/60"
								}`}
								data-testid="enrollment-pause-facts"
							>
								<div>
									<dt className="text-xs opacity-70">
										{t("enrollments.form.pausedAt")}
									</dt>
									<dd className="font-medium font-mono">
										{formatDate(new Date(enrollment.pausedAt).getTime())}
									</dd>
								</div>
								<div>
									<dt className="text-xs opacity-70">
										{t("enrollments.form.pausedDays")}
									</dt>
									<dd className="font-medium">
										{enrollment.status === EnrollmentStatus.PAUSED
											? t("enrollments.form.pausedDaysValue", {
													count: Math.floor(
														(Date.now() -
															new Date(enrollment.pausedAt).getTime()) /
															(1000 * 60 * 60 * 24),
													),
												})
											: enrollment.lastResumedAt
												? t("enrollments.form.pausedDaysValue", {
														count: Math.floor(
															(new Date(enrollment.lastResumedAt).getTime() -
																new Date(enrollment.pausedAt).getTime()) /
																(1000 * 60 * 60 * 24),
														),
													})
												: "—"}
									</dd>
								</div>
								{enrollment.lastResumedAt && (
									<div>
										<dt className="text-xs opacity-70">
											{t("enrollments.form.lastResumedAt")}
										</dt>
										<dd className="font-medium font-mono">
											{formatDate(new Date(enrollment.lastResumedAt).getTime())}
										</dd>
									</div>
								)}
							</dl>
						)}

						{/* Included Services */}
						{enrollment.customerPlan.features &&
							enrollment.customerPlan.features.length > 0 && (
								<div>
									<p className="text-xs text-base-content/60 mb-2">
										{t("common.features")}
									</p>
									<div className="flex flex-wrap gap-1.5">
										{enrollment.customerPlan.features.map((feature) => (
											<Badge
												key={feature}
												variant="outline"
												className="text-xs font-normal text-base-content/80 border-base-300"
											>
												{feature}
											</Badge>
										))}
									</div>
								</div>
							)}

						<div className="flex items-center gap-2 text-xs text-base-content/60">
							<Info className="h-4 w-4 shrink-0 text-base-content/40" />
							<span className="line-clamp-3 wrap-break-words">
								{t("enrollments.edit.customerPlanCannotBeChanged")}
							</span>
						</div>
					</div>

					<div className="border-t border-base-300" />

					{/* Schedules */}
					<div className="space-y-3">
						<div className="flex flex-wrap items-center justify-between gap-2">
							<p className="text-sm font-semibold text-base-content">
								{t("enrollments.sections.schedules")}
							</p>
							{!isEditingSchedules && (
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={() => {
										setTempSelectedScheduleIds([...selectedScheduleIds]);
										setIsEditingSchedules(true);
									}}
								>
									<Edit2 className="h-4 w-4 mr-2" />
									{t("enrollments.edit.editSchedules")}
								</Button>
							)}
						</div>

						{!isEditingSchedules ? (
							// Read-only view: Show selected schedules
							selectedScheduleIds.length > 0 ? (
								<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
									{schedulesData
										.filter((s) => selectedScheduleIds.includes(s.id))
										.map((schedule) => (
											<ScheduleCard
												key={schedule.id}
												schedule={schedule}
												isSelected={false}
												isSelectable={false}
												mode="editingExistingEnrollment"
												onSelect={() => {}}
												headerAction={
													<Link
														to="/app/org/$orgId/schedules/$scheduleId"
														params={{
															orgId,
															scheduleId: schedule.id,
														}}
														className="text-base-content/40 hover:text-primary transition-colors shrink-0"
														title={t("enrollments.edit.viewSchedule")}
													>
														<ExternalLink className="h-3.5 w-3.5" />
													</Link>
												}
											/>
										))}
								</div>
							) : (
								<div
									className={`alert flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between ${
										isClassArchived ? "" : "alert-warning"
									}`}
									role="alert"
									data-testid="enrollment-no-schedule-alert"
								>
									<div className="flex items-start gap-2">
										{isClassArchived ? (
											<Archive className="h-5 w-5 shrink-0" />
										) : (
											<AlertTriangle className="h-5 w-5 shrink-0" />
										)}
										<div className="space-y-0.5">
											<p className="text-sm font-medium">
												{t(
													isClassArchived
														? "enrollments.edit.classArchivedAlertTitle"
														: "enrollments.edit.noScheduleAlertTitle",
												)}
											</p>
											<p className="text-xs opacity-80">
												{t(
													isClassArchived
														? "enrollments.edit.classArchivedAlertDescription"
														: "enrollments.edit.noScheduleAlertDescription",
												)}
											</p>
										</div>
									</div>
									<Button
										type="button"
										size="sm"
										onClick={() => {
											setTempSelectedScheduleIds([...selectedScheduleIds]);
											setIsEditingSchedules(true);
										}}
										className="w-full sm:w-auto"
										data-testid="enrollment-attach-schedule-button"
									>
										<CalendarPlus className="h-4 w-4 mr-2" />
										{t("enrollments.edit.attachSchedule")}
									</Button>
								</div>
							)
						) : (
							// Edit mode: Show all schedules with selection
							<>
								<p className="text-xs text-base-content/60">
									{t("enrollments.edit.selectSchedules")}
								</p>

								{schedulesData && schedulesData.length > 0 ? (
									<FilterableCardGrid
										data={schedulesData}
										displayedData={schedulesData}
										searchTerm=""
										onSearchChange={() => {}}
										hasMore={false}
										onShowMore={() => {}}
										totalCount={schedulesData.length}
										filteredCount={schedulesData.length}
										displayedCount={schedulesData.length}
										renderCard={(schedule) => (
											<ScheduleCard
												key={schedule.id}
												schedule={schedule}
												isSelected={tempSelectedScheduleIds.includes(
													schedule.id,
												)}
												isSelectable={true}
												mode="editingExistingEnrollment"
												isCurrent={originalScheduleIds.has(schedule.id)}
												onSelect={(scheduleId) => {
													setTempSelectedScheduleIds((prev) =>
														prev.includes(scheduleId)
															? prev.filter((id) => id !== scheduleId)
															: [...prev, scheduleId],
													);
												}}
											/>
										)}
										gridClassName="grid grid-cols-1 md:grid-cols-2 gap-3"
										showFiltersThreshold={999}
									/>
								) : (
									<div className="flex items-center justify-center p-6 border border-base-300 rounded-md bg-base-200/30">
										<p className="text-sm text-base-content/60">
											{t("enrollments.edit.noSchedulesAvailable")}
										</p>
									</div>
								)}

								{tempSelectedScheduleIds.length > 0 && (
									<p className="text-xs text-base-content/60">
										{t("enrollments.edit.selectedSchedulesCount", {
											count: tempSelectedScheduleIds.length,
										})}
									</p>
								)}

								{/* Weekly Sessions Limit Error Display */}
								{tempScheduleValidation?.isExceeded && (
									<div
										className="rounded-md bg-error p-3 text-error-content"
										data-testid="schedule-weekly-sessions-limit-error"
									>
										<div className="flex items-start gap-2">
											<AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
											<div className="flex-1 space-y-1">
												<h4 className="text-sm font-semibold">
													{t("enrollments.validation.weeklySessionsLimitTitle")}
												</h4>
												<p className="text-sm opacity-90">
													{t(
														"enrollments.validation.weeklySessionsLimitDescription",
														{
															total: tempScheduleValidation.totalWeeklySessions,
															limit: tempScheduleValidation.weeklySessionsLimit,
															planName: enrollment.customerPlan.name,
														},
													)}
												</p>
											</div>
										</div>
									</div>
								)}

								{/* Edit mode action buttons */}
								<div className="flex gap-2">
									<Button
										type="button"
										variant="default"
										size="sm"
										onClick={() => {
											setSelectedScheduleIds([...tempSelectedScheduleIds]);
											setIsEditingSchedules(false);
										}}
										disabled={tempScheduleValidation?.isExceeded}
									>
										<Save className="h-4 w-4 mr-2" />
										{t("enrollments.edit.saveScheduleChanges")}
									</Button>
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => {
											setTempSelectedScheduleIds([]);
											setIsEditingSchedules(false);
										}}
									>
										<X className="h-4 w-4 mr-2" />
										{t("common.cancel")}
									</Button>
								</div>
							</>
						)}
					</div>

					<div className="border-t border-base-300" />

					{/* Notes */}
					<div className="space-y-2">
						<label
							htmlFor="enrollment-notes"
							className="text-sm font-semibold text-base-content"
						>
							{t("enrollments.form.notes")}
						</label>
						<Textarea
							id="enrollment-notes"
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
							placeholder={t("enrollments.form.notesPlaceholder")}
							className="min-h-25 resize-none"
							disabled={isUpdating}
							data-testid="notes-textarea"
						/>
					</div>
				</CardContent>
			</Card>

			{/* Cancel Enrollment Modal */}
			{isCancelModalOpen && (
				<EnrollmentCancelationModal
					isOpen={isCancelModalOpen}
					onClose={handleCloseCancelModal}
					onConfirm={handleConfirmCancel}
					enrollmentId={enrollment.id}
					schedules={enrollment.schedules}
					customerName={`${enrollment.customer.firstName} ${enrollment.customer.lastName}`}
					organizationId={orgId}
					token={token}
					isSubmitting={isCanceling}
				/>
			)}

			{/* Pause Enrollment Modal */}
			{isPauseModalOpen && (
				<EnrollmentPauseModal
					isOpen={isPauseModalOpen}
					onClose={handleClosePauseModal}
					onConfirm={handleConfirmPause}
					customerName={`${enrollment.customer.firstName} ${enrollment.customer.lastName}`}
					isSubmitting={isPausing}
				/>
			)}

			{/* Extend Enrollment Modal */}
			{isExtendModalOpen && enrollment.endDate && (
				<EnrollmentExtendModal
					isOpen={isExtendModalOpen}
					onClose={handleCloseExtendModal}
					onConfirm={handleConfirmExtend}
					customerName={`${enrollment.customer.firstName} ${enrollment.customer.lastName}`}
					currentEndDate={enrollment.endDate}
					billingCycle={enrollment.customerPlan.billingCycle as BillingCycle}
					isSubmitting={isExtending}
				/>
			)}

			{/* Resume Enrollment Modal */}
			{isResumeModalOpen && enrollment.pausedAt && (
				<EnrollmentResumeModal
					isOpen={isResumeModalOpen}
					onClose={handleCloseResumeModal}
					onConfirm={handleConfirmResume}
					customerName={`${enrollment.customer.firstName} ${enrollment.customer.lastName}`}
					isSubmitting={isUnpausing}
					pausedAt={new Date(enrollment.pausedAt)}
					endDate={
						enrollment.endDate ? new Date(enrollment.endDate) : undefined
					}
					token={token}
					organizationId={orgId}
					requiresScheduleAttachment={
						(enrollment.activeScheduleLinkCount ?? 0) === 0
					}
				/>
			)}
		</div>
	);
}
